import {useMutation, useQueryClient} from '@github-ui/react-query'
import {createEstablishedAhpInitialSubmissionCoordinator} from '@github-ui/ahp-session-client/initial-submission'
import type {PreparedPrompt} from '@github-ui/ahp-session-client/contracts'
import {fetchTask} from './use-task'
import {readTasks, writeTasks, type TaskListPage} from './repo-task-utils'
import type {HydratedAPITask} from '../types/api-task'
import type {McpPermissionMode} from '@github-ui/agent-sessions-types/mcp-permission-mode'
import type {OptimisticTask} from '../types/task'
import {AhpChatStore} from '@github-ui/ahp-relay/ahp-chat-store'
import {reportRelayError} from '@github-ui/ahp-relay/relay-registry'
import {isFeatureEnabled} from '@github-ui/feature-flags'
import {mapMcpPermissionModeToAgentMode} from '../utils/mcp-permission-mode'
import {mcFetcher} from '../utils/mc-fetcher'
import {repoCloneUrl} from '../utils/repo-clone-url'
import {fetchCAPI} from '@github-ui/copilot-shared-ui/capi/fetch-capi'
import {tasksApiPath} from '../utils/api-url-helpers'
import {sortTasksByDate, transformAPITaskToUnhydrated} from '../utils/task-helpers'
import {uuid} from '../utils/uuid'
import {createMissionControlEstablishedAhpBootstrap} from '../ahp/established-ahp-bootstrap'
import {createAhpRelayDiagnostics} from '../ahp/ahp-debug-log'
import {beginTaskStartup, completeTaskStartup, failTaskStartup} from '../ahp/task-startup-store'

export interface CreateEnvironmentTaskInput {
  environmentId: string
  prompt: PreparedPrompt
  /** Repository (name-with-owner) to associate + provision as a host project. */
  repositoryNwo?: string
  /** Server-provided HTTPS clone URL; host-correct, preferred over the origin-derived {@link repoCloneUrl}. */
  repositoryCloneUrl?: string
  /** Base branch for the worktree; the host auto-generates the working branch. */
  baseRef?: string
  /** Managed sandbox VM; client-side host-project provisioning is flag-gated. */
  managedSandbox?: boolean
  /**
   * Optional MCP permission mode captured by the composer. When set to a
   * non-default value, the first-turn coordinator applies the equivalent
   * chat runtime mode via `session.commands.setMode` before accepting the
   * user's opening turn, mirroring the Quick Chat handoff.
   */
  mcpPermissionMode?: McpPermissionMode
  clientGeneratedId?: string
}

export interface CreateEnvironmentTaskResult {
  task: HydratedAPITask
  sessionId: string
  environmentId: string
}

/**
 * Cache integration that mirrors the cloud `useCreateAPITask` shape so each
 * page can pass the same optimistic config it already builds for cloud task
 * creation. When omitted, the hook still works — it just doesn't touch any
 * task list cache.
 */
export interface OptimisticTaskListConfig {
  /** Query key whose data this hook should optimistically modify. */
  queryKey: readonly unknown[]
  /** Additional query keys to invalidate (not optimistically update) after a
   * successful creation — e.g. the grouped-tasks sidebar cache. */
  additionalInvalidateKeys?: ReadonlyArray<readonly unknown[]>
  /** Maximum number of tasks to keep in the list after the optimistic
   * placeholder is replaced; matches the page's pagination size so the list
   * doesn't overflow. */
  pageSize: number
}

export interface UseCreateEnvironmentTaskOptions {
  optimisticTaskListConfig?: OptimisticTaskListConfig
}

interface MutationContext {
  clientGeneratedId: string
}

export function useCreateEnvironmentTask(options?: UseCreateEnvironmentTaskOptions) {
  const queryClient = useQueryClient()
  const config = options?.optimisticTaskListConfig

  return useMutation<CreateEnvironmentTaskResult, Error, CreateEnvironmentTaskInput, MutationContext>({
    onMutate: async (input): Promise<MutationContext> => {
      const clientGeneratedId = input.clientGeneratedId ?? uuid()
      input.clientGeneratedId = clientGeneratedId
      if (!config) return {clientGeneratedId}

      await queryClient.cancelQueries({queryKey: config.queryKey})
      const optimistic = createOptimisticEnvironmentTask({
        clientGeneratedId,
        problemStatement: input.prompt.text,
      })
      queryClient.setQueryData<TaskListPage>(config.queryKey, oldData =>
        writeTasks(oldData, [optimistic, ...readTasks(oldData)].slice(0, config.pageSize)),
      )
      return {clientGeneratedId}
    },
    mutationFn: async ({
      environmentId,
      prompt,
      repositoryNwo,
      repositoryCloneUrl,
      baseRef,
      managedSandbox,
      mcpPermissionMode,
      clientGeneratedId,
    }): Promise<CreateEnvironmentTaskResult> => {
      // MC binds the session row (and associates the repo); daemon session creation is ours.
      // For the on-demand `github-sandbox` sentinel, MC provisions a concrete environment; use its id below.
      // The prompt rides along as `prompt` so MC persists it on the session (event_content),
      // letting a hard-navigated task view recover the first turn without client-side storage.
      const {taskId, sessionId, boundEnvironmentId} = await createEnvironmentBoundTask({
        environmentId,
        problemStatement: prompt.text,
        repositoryNwo,
        baseRef,
      })

      if (config && clientGeneratedId) {
        patchOptimisticTaskId(queryClient, config, clientGeneratedId, taskId)
      }

      const relayEnvironmentId = boundEnvironmentId ?? environmentId
      const identity = {taskId, environmentId: relayEnvironmentId, sessionId}
      beginTaskStartup(taskId, prompt)

      const bootstrap = createMissionControlEstablishedAhpBootstrap({
        provisionSession: async () => {
          throw new Error('Environment task bootstrap cannot provision a second task.')
        },
        createRelayOptions: (relayIdentity, request) => ({
          fetcher: mcFetcher,
          createSink: () => new AhpChatStore(),
          ...createAhpRelayDiagnostics(relayIdentity.taskId ?? relayIdentity.sessionId),
          // Sandbox VMs are pre-cloned by MC; client-side host-project
          // provisioning is opt-in until the host advertises project management.
          ...(request?.mode === 'recover'
            ? {
                createSession:
                  repositoryNwo &&
                  (!managedSandbox || isFeatureEnabled('copilot_mission_control_sandbox_client_side_clone'))
                    ? {
                        project: {
                          nwo: repositoryNwo,
                          cloneUrl: repoCloneUrl(repositoryNwo, repositoryCloneUrl),
                          ...(baseRef ? {baseBranch: baseRef} : {}),
                        },
                      }
                    : {},
              }
            : {}),
        }),
      })
      const initialSubmission = createEstablishedAhpInitialSubmissionCoordinator({
        bootstrap,
        identity,
      })
      // Apply mode via configureSession so it's scoped to the first-turn coordinator, mirroring EstablishedChatComposer.
      // Wire it whenever the picker supplied a mode (including 'default' = Interactive) so we can override
      // the relay's connect-time approveAll=true default. Skipping 'default' here would leave tool calls
      // auto-approving even when the user explicitly picked Interactive.
      const configureSessionMode = mcpPermissionMode ? mapMcpPermissionModeToAgentMode(mcpPermissionMode) : undefined
      void (async () => {
        try {
          const establishedSession = await initialSubmission.submit({
            prompt,
            origin: 'new',
            ...(configureSessionMode
              ? {
                  configureSession: async session => {
                    // setMode and setApproveAll are independent host RPCs. The AHP relay
                    // default-enables approveAll on connect (relay-registry.ts:422), so
                    // Interactive/Plan must also explicitly disable it or tool calls auto-approve.
                    // Setting it here marks the entry so the default can't re-fire when TaskChat's
                    // lease reuses the entry within the ref-count grace period.
                    await Promise.all([
                      session.commands.setMode(configureSessionMode),
                      session.commands.setApproveAll(configureSessionMode === 'autopilot'),
                    ])
                  },
                }
              : {}),
          })
          if (establishedSession.view.getSnapshot().passive) {
            throw new Error('This environment is read-only.')
          }
          completeTaskStartup(taskId)
        } catch (error) {
          failTaskStartup(taskId, error)
          reportRelayError(taskId, error)
        } finally {
          initialSubmission.dispose()
          bootstrap.dispose()
        }
      })()

      const task = await fetchTask({taskId})
      return {
        task,
        sessionId,
        environmentId: relayEnvironmentId,
      }
    },
    onSuccess: ({task}, _input, context) => {
      if (!config || !context) return
      const transformed = transformAPITaskToUnhydrated(task)
      queryClient.setQueryData<TaskListPage>(config.queryKey, oldData => {
        const cached = readTasks(oldData)
        const withoutDupes = cached.filter(
          t =>
            t.clientGeneratedId !== context.clientGeneratedId &&
            (!transformed || (t.id !== transformed.id && t.task_id !== transformed.id)),
        )
        const next = transformed ? sortTasksByDate([transformed, ...withoutDupes]) : withoutDupes
        return writeTasks(oldData, next.slice(0, config.pageSize))
      })
      queryClient.invalidateQueries({queryKey: config.queryKey, exact: true})

      for (const key of config.additionalInvalidateKeys ?? []) {
        queryClient.invalidateQueries({queryKey: key})
      }
    },
    onError: (_error, _input, context) => {
      if (!config || !context) return
      queryClient.setQueryData<TaskListPage>(config.queryKey, oldData =>
        writeTasks(
          oldData,
          readTasks(oldData).filter(t => t.clientGeneratedId !== context.clientGeneratedId),
        ),
      )
    },
  })
}

/** Minimal shape of the `POST /agents/tasks` (GetTaskResponse) body we depend on. */
interface EnvironmentTaskResponse {
  id?: string
  task?: {id?: string}
  sessions?: Array<{id?: string; environment_id?: string}>
}

// Binds a task + session to an existing environment via `POST /agents/tasks`.
// MC starts no run (the caller drives it over the relay) and, when a repo is
// provided, associates the task with it so it lists under that repository.
async function createEnvironmentBoundTask({
  environmentId,
  problemStatement,
  repositoryNwo,
  baseRef,
}: {
  environmentId: string
  problemStatement: string
  repositoryNwo?: string
  baseRef?: string
}): Promise<{taskId: string; sessionId: string; boundEnvironmentId?: string}> {
  const repository = parseNwo(repositoryNwo)
  const response = await fetchCAPI({
    path: tasksApiPath(),
    method: 'POST',
    additionalHeaders: {Accept: 'application/json'},
    body: {
      environment_id: environmentId,
      // MC resolves `prompt` into the session's event_content; it starts no run
      // for these client-driven tasks, so this only persists the first turn.
      prompt: problemStatement,
      ...(repository ? {repositories: [repository]} : {}),
      ...(baseRef ? {base_ref: baseRef} : {}),
    },
  })
  const body = (await response.json()) as EnvironmentTaskResponse

  const taskId = body.id ?? body.task?.id
  if (!taskId) {
    throw new Error('Mission Control did not return a task id for this environment task')
  }
  const session = body.sessions?.[0]
  if (!session?.id) {
    throw new Error('Mission Control did not create a session for this environment')
  }
  // The concrete environment MC bound (for on-demand sandbox this is the newly
  // provisioned VM, not the `github-sandbox` sentinel we requested).
  return {taskId, sessionId: session.id, boundEnvironmentId: session.environment_id}
}

function patchOptimisticTaskId(
  queryClient: ReturnType<typeof useQueryClient>,
  config: OptimisticTaskListConfig,
  clientGeneratedId: string,
  taskId: string,
): void {
  queryClient.setQueryData<TaskListPage>(config.queryKey, oldData => {
    const cached = readTasks(oldData)
    if (!cached.some(task => task.clientGeneratedId === clientGeneratedId)) return oldData
    return writeTasks(
      oldData,
      cached.map(task =>
        task.clientGeneratedId === clientGeneratedId ? {...task, id: taskId, task_id: taskId} : task,
      ),
    )
  })
}

/** Split an `owner/repo` nwo into the `{owner, name}` MC expects, or undefined. */
function parseNwo(nwo: string | undefined): {owner: string; name: string} | undefined {
  if (!nwo) return undefined
  const [owner, name] = nwo.split('/')
  if (!owner || !name) return undefined
  return {owner, name}
}

interface CreateOptimisticEnvironmentTaskParams {
  clientGeneratedId: string
  problemStatement: string
}

function createOptimisticEnvironmentTask({
  clientGeneratedId,
  problemStatement,
}: CreateOptimisticEnvironmentTaskParams): OptimisticTask {
  return {
    resource_type: 'pull',
    resource_id: -1,
    resource_global_id: '',
    created_at: new Date().toISOString(),
    session_count: 1,
    latest_session_updated_at: new Date().toISOString(),
    latest_session_state: 'in_progress',
    isOptimistic: true,
    clientGeneratedId,
    problemStatement,
    name: problemStatement,
  }
}
